import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { LoginContext } from './LoginContext';
import { showToast } from '../Components/utils/ShowToast';

const CartContext = createContext();

export function CartProvider({ children }) {
  const { isLogin, user } = useContext(LoginContext);
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [cartActionLoading, setCartActionLoading] = useState(false);
  const [taxRate, setTaxRate] = useState(0);

  useEffect(() => {
    if (isLogin) {
      fetchCart();
    } else {
      setCartItems([]);
      setTaxRate(0);
    }
  }, [isLogin]);

  useEffect(() => {
    if (!isLogin) return;

    const updateCartDetails = async () => {
      try {
        await fetchCart();
      } catch (error) {
        console.error('Failed to update cart details:', error);
      }
    };

    updateCartDetails();
    const intervalId = setInterval(updateCartDetails, 300000);
    return () => clearInterval(intervalId);
  }, [isLogin]);

  const fetchCart = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/cart`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setCartItems(response.data.cartItems || []);
      setTaxRate(response.data.taxRate || 0);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const cartLineKey = (row) =>
    JSON.stringify({
      productId: row.productId,
      selectedOptions: row.selectedOptions,
      deliveryType: row.deliveryType,
      pricingType: row.pricingType,
      isB2B: Boolean(row.isB2B),
      addedServicesTotal: Number(row.addedServicesTotal) || 0,
      addedServices: row.addedServices || [],
    });

  const computeDeliveryFeeFromProduct = (product) => {
    const sellerAddress = product?.sellerId?.sellerAddress || {};
    const buyerAddress = user?.buyerAddress || {};
    const sellerCountry = (sellerAddress?.country?.name || '').toLowerCase();
    const buyerCountry = (buyerAddress?.country?.name || '').toLowerCase();
    const sellerRegion = (sellerAddress?.state?.name || '').toLowerCase();
    const buyerRegion = (buyerAddress?.state?.name || '').toLowerCase();

    if (product?.listingType === 'service' && product?.serviceMode === 'online') {
      return 0;
    }
    if (product?.sellingScope === 'international') {
      if (sellerCountry && buyerCountry && sellerCountry !== buyerCountry) {
        return Number(product?.shippingFees?.worldwide) || 0;
      }
      if (sellerRegion && buyerRegion && sellerRegion === buyerRegion) {
        return Number(product?.shippingFees?.nearby) || 0;
      }
      return Number(product?.shippingFees?.withinCountry) || 0;
    }
    return parseFloat(product.deliveryFee) || 0;
  };

  const computeDeliveryFeeFromB2BListing = (listing) => {
    const sellerAddress = listing?.b2bSellerId?.sellerId?.sellerAddress || {};
    const buyerAddress = user?.buyerAddress || {};
    const sellerCountry = (sellerAddress?.country?.name || '').toLowerCase();
    const buyerCountry = (buyerAddress?.country?.name || '').toLowerCase();
    const sellerRegion = (sellerAddress?.state?.name || '').toLowerCase();
    const buyerRegion = (buyerAddress?.state?.name || '').toLowerCase();

    if (listing?.listingType === 'service') {
      return 0;
    }
    if (listing?.sellingScope === 'international') {
      if (sellerCountry && buyerCountry && sellerCountry !== buyerCountry) {
        return Number(listing?.shippingFees?.worldwide) || 0;
      }
      if (sellerRegion && buyerRegion && sellerRegion === buyerRegion) {
        return Number(listing?.shippingFees?.nearby) || 0;
      }
      return Number(listing?.shippingFees?.withinCountry) || 0;
    }
    return parseFloat(listing?.deliveryFee) || 0;
  };

  const addToCart = async (item) => {
    try {
      setCartActionLoading(true);
      const {
        productId,
        quantity,
        selectedOptions,
        price,
        name,
        thumbnail,
        deliveryType,
        pricingType,
        addedServices,
        addedServicesTotal,
        isB2B,
      } = item;

      let deliveryFee = 0;
      if (deliveryType === 'delivery') {
        if (isB2B) {
          const { data } = await axios.get(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/listings/${productId}`
          );
          deliveryFee = computeDeliveryFeeFromB2BListing(data.listing || {});
        } else {
          const { data } = await axios.get(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/product/${productId}`
          );
          deliveryFee = computeDeliveryFeeFromProduct(data.product || {});
        }
      }

      const incoming = {
        productId,
        selectedOptions,
        deliveryType,
        pricingType,
        isB2B: Boolean(isB2B),
        addedServicesTotal: Number(addedServicesTotal) || 0,
        addedServices: addedServices || [],
      };

      const existingItem = cartItems.find((i) => cartLineKey(i) === cartLineKey(incoming));
      if (existingItem) {
        showToast('Product is already in Cart', 'error');
        return;
      }

      const row = {
        productId,
        quantity,
        selectedOptions,
        price,
        name,
        thumbnail,
        deliveryType,
        deliveryFee,
        pricingType,
        addedServices: addedServices || [],
        addedServicesTotal: Number(addedServicesTotal) || 0,
        isB2B: Boolean(isB2B),
      };

      setCartItems((prevItems) => [...prevItems, row]);

      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/cart/add`,
        row,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) showToast('Added to cart successfully!', 'success');

      setCartItems(response.data.cartItems);
    } catch (error) {
      showToast('Failed to add to cart', 'error');
      fetchCart();
    } finally {
      setCartActionLoading(false);
    }
  };

  const removeFromCart = async (productId, cartItemId) => {
    try {
      setCartActionLoading(true);
      setCartItems((prevItems) =>
        cartItemId
          ? prevItems.filter((item) => String(item._id) !== String(cartItemId))
          : prevItems.filter((item) => String(item.productId) !== String(productId))
      );

      const response = await axios.delete(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/cart/remove`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        data: cartItemId ? { cartItemId } : { productId },
      });
      if (response.data.success) {
        showToast('Product removed from Cart', 'success');
        if (response.data.cartItems) setCartItems(response.data.cartItems);
      }
    } catch (error) {
      showToast('Failed to remove from cart', 'error');
      fetchCart();
    } finally {
      setCartActionLoading(false);
    }
  };

  const updateQuantity = async (productId, selectedOptions, deliveryType, newQuantity, cartItemId) => {
    if (newQuantity < 1) return;

    setCartItems((prevItems) =>
      prevItems.map((item) => {
        // If cartItemId is provided, match exactly by _id (handles multi-variation same product)
        if (cartItemId) {
          return String(item._id) === String(cartItemId) ? { ...item, quantity: newQuantity } : item;
        }
        // Fallback: match by productId + selectedOptions + deliveryType
        return item.productId === productId &&
          JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions) &&
          item.deliveryType === deliveryType
          ? { ...item, quantity: newQuantity }
          : item;
      })
    );

    try {
      await axios.put(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/cart/update`,
        cartItemId
          ? { cartItemId, quantity: newQuantity }
          : { productId, selectedOptions, deliveryType, quantity: newQuantity },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
    } catch (error) {
      showToast('Failed to update quantity', 'error');
      fetchCart();
    }
  };

  const clearCart = async () => {
    setCartItems([]);
    try {
      await axios.delete(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/cart/clear`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
    } catch (error) {
      showToast('Failed to clear cart', 'error');
      fetchCart();
    }
  };

  /** Remove specific cart lines by Mongo _id (e.g. after checkout). Does not toast per line. */
  const removeCartLinesByIds = async (cartItemIds) => {
    if (!cartItemIds?.length) return;
    const ids = [...new Set(cartItemIds.map((id) => String(id)))];
    try {
      setCartActionLoading(true);
      const token = localStorage.getItem('token');
      setCartItems((prev) => prev.filter((item) => !ids.includes(String(item._id))));
      let lastCartItems;
      for (const cartItemId of ids) {
        const { data } = await axios.delete(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/cart/remove`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { cartItemId },
        });
        if (data?.cartItems) lastCartItems = data.cartItems;
      }
      if (lastCartItems) setCartItems(lastCartItems);
    } catch (error) {
      showToast('Could not update cart after order', 'error');
      fetchCart();
    } finally {
      setCartActionLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        removeCartLinesByIds,
        isLoading,
        taxRate,
        cartActionLoading
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}